<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('File storage') }}</h1>
                </div>
                <div class="mt-4 flex md:mt-0 md:ml-4">
                    <router-link
                        class="btn btn-blue shadow-sm rounded-md"
                        to="/dashboard/admin/settings"
                    >
                        {{ $t('Back') }}
                    </router-link>
                </div>
            </div>
        </div>
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="my-6">
                <loading :status="loading"/>
                <form @submit.prevent="save">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="px-4 py-5 bg-white sm:p-6">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="file_storage_disk">
                                        {{ $t('Storage driver') }}
                                    </label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <select
                                            id="file_storage_disk"
                                            v-model="file_storage_disk"
                                            class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
                                            required
                                        >
                                            <option value="local">{{ $t('Local') }}</option>
                                            <option value="s3">{{ $t('Amazon S3 / S3-compatible') }}</option>
                                        </select>
                                    </div>
                                    <p class="mt-1 text-xs text-gray-500">
                                        {{ $t('Select where uploaded files should be stored. Existing files remain on their original storage.') }}
                                    </p>
                                </div>

                                <template v-if="file_storage_disk === 's3'">
                                    <div class="col-span-3">
                                        <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('S3 Configuration') }}</h3>
                                        <p class="mt-1 text-sm text-gray-500">{{ $t('Configure your S3 or S3-compatible storage (MinIO, DigitalOcean Spaces, etc.)') }}</p>
                                    </div>
                                    <div class="col-span-3 sm:col-span-1">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="aws_access_key_id">
                                            {{ $t('Access Key ID') }}
                                        </label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="aws_access_key_id"
                                                v-model="aws_access_key_id"
                                                class="form-input block w-full sm:text-sm sm:leading-5"
                                                type="text"
                                            >
                                        </div>
                                    </div>
                                    <div class="col-span-3 sm:col-span-1">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="aws_secret_access_key">
                                            {{ $t('Secret Access Key') }}
                                        </label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="aws_secret_access_key"
                                                v-model="aws_secret_access_key"
                                                class="form-input block w-full sm:text-sm sm:leading-5"
                                                type="password"
                                            >
                                        </div>
                                    </div>
                                    <div class="col-span-3 sm:col-span-1">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="aws_default_region">
                                            {{ $t('Region') }}
                                        </label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="aws_default_region"
                                                v-model="aws_default_region"
                                                class="form-input block w-full sm:text-sm sm:leading-5"
                                                placeholder="us-east-1"
                                                type="text"
                                            >
                                        </div>
                                    </div>
                                    <div class="col-span-3 sm:col-span-1">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="aws_bucket">
                                            {{ $t('Bucket') }}
                                        </label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="aws_bucket"
                                                v-model="aws_bucket"
                                                class="form-input block w-full sm:text-sm sm:leading-5"
                                                type="text"
                                            >
                                        </div>
                                    </div>
                                    <div class="col-span-3 sm:col-span-2">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="aws_endpoint">
                                            {{ $t('Endpoint (optional)') }}
                                        </label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <input
                                                id="aws_endpoint"
                                                v-model="aws_endpoint"
                                                class="form-input block w-full sm:text-sm sm:leading-5"
                                                placeholder="https://s3.example.com"
                                                type="text"
                                            >
                                        </div>
                                        <p class="mt-1 text-xs text-gray-500">
                                            {{ $t('Leave empty for Amazon S3. Set for S3-compatible services like MinIO or DigitalOcean Spaces.') }}
                                        </p>
                                    </div>
                                    <div class="col-span-3 sm:col-span-1">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="aws_use_path_style">
                                            {{ $t('Path style endpoint') }}
                                        </label>
                                        <div class="mt-2">
                                            <label class="inline-flex items-center">
                                                <input
                                                    id="aws_use_path_style"
                                                    v-model="aws_use_path_style"
                                                    class="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                                                    type="checkbox"
                                                    true-value="1"
                                                    false-value="0"
                                                >
                                                <span class="ml-2 text-sm text-gray-700">{{ $t('Enable') }}</span>
                                            </label>
                                        </div>
                                        <p class="mt-1 text-xs text-gray-500">
                                            {{ $t('Enable for S3-compatible servers that use path-style URLs (e.g. MinIO).') }}
                                        </p>
                                    </div>
                                    <div class="col-span-3 sm:col-span-1">
                                        <label class="block text-sm font-medium leading-5 text-gray-700" for="aws_signature_version">
                                            {{ $t('Signature version') }}
                                        </label>
                                        <div class="mt-1 relative rounded-md shadow-sm">
                                            <select
                                                id="aws_signature_version"
                                                v-model="aws_signature_version"
                                                class="mt-1 form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5"
                                            >
                                                <option value="v4">v4</option>
                                                <option value="v2">v2</option>
                                            </select>
                                        </div>
                                        <p class="mt-1 text-xs text-gray-500">
                                            {{ $t('Use v2 for older S3-compatible servers, v4 is recommended for most cases.') }}
                                        </p>
                                    </div>
                                    <div class="col-span-3">
                                        <button
                                            class="btn btn-white shadow-sm rounded-md"
                                            type="button"
                                            :disabled="testing"
                                            @click="testConnection"
                                        >
                                            <span v-if="testing">{{ $t('Testing...') }}</span>
                                            <span v-else>{{ $t('Test connection') }}</span>
                                        </button>
                                        <span v-if="testResult" class="ml-3 text-sm" :class="testResult.success ? 'text-green-600' : 'text-red-600'">
                                            {{ testResult.message }}
                                        </span>
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div class="px-4 py-3 bg-gray-100 text-right sm:px-6">
                            <div class="inline-flex">
                                <button class="btn btn-green rounded-md shadow-sm" type="submit">
                                    {{ $t('Save settings') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "file-storage",
    data() {
        return {
            loading: true,
            testing: false,
            testResult: null,
            file_storage_disk: 'local',
            aws_access_key_id: null,
            aws_secret_access_key: null,
            aws_default_region: 'us-east-1',
            aws_bucket: null,
            aws_endpoint: null,
            aws_use_path_style: '0',
            aws_signature_version: 'v4',
        }
    },
    mounted() {
        this.get();
    },
    methods: {
        get() {
            const self = this;
            axios.get('api/dashboard/admin/settings/file-storage').then(function (response) {
                self.file_storage_disk = response.data.file_storage_disk || 'local';
                self.aws_access_key_id = response.data.aws_access_key_id;
                self.aws_secret_access_key = response.data.aws_secret_access_key;
                self.aws_default_region = response.data.aws_default_region || 'us-east-1';
                self.aws_bucket = response.data.aws_bucket;
                self.aws_endpoint = response.data.aws_endpoint;
                self.aws_use_path_style = response.data.aws_use_path_style || '0';
                self.aws_signature_version = response.data.aws_signature_version || 'v4';
                self.loading = false;
            }).catch(function () {
                self.$router.push('/dashboard/admin/settings');
            });
        },
        save() {
            const self = this;
            self.loading = true;
            axios.post('api/dashboard/admin/settings/file-storage', {
                file_storage_disk: self.file_storage_disk,
                aws_access_key_id: self.aws_access_key_id,
                aws_secret_access_key: self.aws_secret_access_key,
                aws_default_region: self.aws_default_region,
                aws_bucket: self.aws_bucket,
                aws_endpoint: self.aws_endpoint,
                aws_use_path_style: self.aws_use_path_style,
                aws_signature_version: self.aws_signature_version,
            }).then(function (response) {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: response.data.message,
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        },
        testConnection() {
            const self = this;
            self.testing = true;
            self.testResult = null;
            axios.post('api/dashboard/admin/settings/file-storage/test', {
                aws_access_key_id: self.aws_access_key_id,
                aws_secret_access_key: self.aws_secret_access_key,
                aws_default_region: self.aws_default_region,
                aws_bucket: self.aws_bucket,
                aws_endpoint: self.aws_endpoint,
                aws_use_path_style: self.aws_use_path_style,
                aws_signature_version: self.aws_signature_version,
            }).then(function (response) {
                self.testing = false;
                self.testResult = { success: true, message: response.data.message };
            }).catch(function (error) {
                self.testing = false;
                var msg = self.$i18n.t('Connection failed');
                if (error.response && error.response.data && error.response.data.message) {
                    msg = error.response.data.message;
                }
                self.testResult = { success: false, message: msg };
            });
        }
    }
}
</script>
